import React from 'react'
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination'
import { usePagination, type PaginationData } from '@/hooks/usePagination'

interface PaginationControlsProps {
  paginationData: PaginationData
  onPageChange: (page: number) => void
  onNextPage?: () => void
  onPreviousPage?: () => void
  loading?: boolean
  className?: string
}

export function PaginationControls({
  paginationData,
  onPageChange,
  onNextPage,
  onPreviousPage,
  loading = false,
  className,
}: PaginationControlsProps) {
  const pagination = usePagination(paginationData, {
    maxPagesToShow: 7,
    showEllipsis: true,
  })

  const {
    pages,
    hasNextPage,
    hasPreviousPage,
    currentPage,
    showingFrom,
    showingTo,
    totalItems,
  } = pagination

  // Don't render if there's only one page or no items
  if (pagination.totalPages <= 1 || totalItems === 0) {
    return null
  }

  const handlePreviousClick = () => {
    if (hasPreviousPage && !loading) {
      if (onPreviousPage) {
        onPreviousPage()
      } else {
        onPageChange(currentPage - 1)
      }
    }
  }

  const handleNextClick = () => {
    if (hasNextPage && !loading) {
      if (onNextPage) {
        onNextPage()
      } else {
        onPageChange(currentPage + 1)
      }
    }
  }

  const handlePageClick = (page: number) => {
    if (!loading && page !== currentPage) {
      onPageChange(page)
    }
  }

  return (
    <div className={`flex flex-col items-center space-y-4 ${className || ''}`}>
      {/* Results info */}
      <div className="text-sm text-gray-500">
        Showing {showingFrom.toLocaleString()} to {showingTo.toLocaleString()} of{' '}
        {totalItems.toLocaleString()} results
      </div>

      {/* Pagination navigation */}
      <Pagination>
        <PaginationContent>
          {/* Previous button */}
          <PaginationItem>
            <PaginationPrevious
              onClick={handlePreviousClick}
              className={
                !hasPreviousPage || loading
                  ? 'pointer-events-none opacity-50'
                  : 'cursor-pointer hover:bg-gray-100'
              }
            />
          </PaginationItem>

          {/* Page numbers */}
          {pages.map((page, index) => (
            <PaginationItem key={index}>
              {page === 'ellipsis' ? (
                <PaginationEllipsis />
              ) : (
                <PaginationLink
                  onClick={() => handlePageClick(page)}
                  isActive={page === currentPage}
                  className={
                    loading
                      ? 'pointer-events-none opacity-50'
                      : page === currentPage
                      ? 'bg-vitl-green text-white hover:bg-vitl-green/90'
                      : 'cursor-pointer hover:bg-gray-100'
                  }
                >
                  {page}
                </PaginationLink>
              )}
            </PaginationItem>
          ))}

          {/* Next button */}
          <PaginationItem>
            <PaginationNext
              onClick={handleNextClick}
              className={
                !hasNextPage || loading
                  ? 'pointer-events-none opacity-50'
                  : 'cursor-pointer hover:bg-gray-100'
              }
            />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    </div>
  )
}

export default PaginationControls